// お守り（GAME_DESIGN 18章）：ツリーと装備で得られないルール変化を主効果に持つ、ランダム生成の個体（モンハンの護石風）。
// - 主効果（1つ）：12種から1つ。効果の大きさに幅があり、品質 q（0..1）で決まる。ルール変化（移動の最低歩数+1・建築2回など）は固定で、
//   一緒に付く数値（攻撃・建築費など）の大きさで幅を表す。幅は旧お守りの段1〜段4（CONFIG.omamori.mainSpan。q=0.5 が旧2.5段）。
// - 副効果（0〜3個）：攻撃・HP・防御・速さ・術の威力・建物HP・建物の効果・本陣・中立・妖力の溜まりやすさ。Lv1〜5。
// - レア度：並・良・優・極で、q の幅・副効果の数・Lv の上限が上がる（CONFIG.omamori.rarity）。
// - 装備枠はキャラごとに1（OMAMORI_SLOTS。ルール変化を重ねられると強すぎるため。2026-10-21 に3 → 1）。1つの個体は1キャラだけ。
//   同じ主効果の判定（same_kind）は枠が1つなので実際には起きないが残す。副効果は効果ごとに上限（cap＝Lv5 より1 Lv 上。1つでは当たらない安全柵）。
// - ロック中・装備中・旧課金品は錬成・売却の素材にできない（サーバーはこの関数の判定で必ず弾く）。
import type { RngHolder } from '../rng';
import { CHAR_IDS, type CharId } from '../types';
import { randInt, random, weighted } from '../rng';
import { CONFIG } from './config';
import { addMod, MOD_LABEL, type Effect, type ModKey, type Mods } from './mods';
import { OMAMORI_SLOTS, RARITIES, type OmamoriItem, type OmamoriSub, type OmamoriSubId, type OpResult, type Profile, type Rarity, type Resources } from './profile';

const r3 = (x: number) => Math.round(x * 1000) / 1000;
const clamp01 = (q: number) => Math.max(0, Math.min(1, Number.isFinite(q) ? q : 0.5));
const isPct = (k: ModKey) => !['move', 'range', 'matStart', 'matIncome', 'potionCount', 'perTurn', 'diceMin', 'diceLow', 'diceRoleMat', 'respawn', 'maxCount', 'unlock', 'cd', 'buildRange', 'agiFlat'].includes(k);
const pct = (v: number) => `${(Math.round(v * 1000) / 10).toFixed(1).replace(/\.0$/, '')}%`;

/** 主効果の数値の部分：q=0 で lo（旧お守りの段1）、q=1 で lo+(hi-lo)×mainSpan（hi は旧段5の値。ルール変化の無いお守りは1枠化で旧の約1.6倍） */
export interface OmamoriScale {
  key: ModKey;
  lo: number;
  hi: number;
  /** 'int' なら四捨五入した整数（陣材など） */
  unit?: 'int';
}

export interface OmamoriDef {
  id: string;
  name: string;
  /** ルール変化の説明（固定の部分） */
  rule: string;
  /** ルール変化（q によらない） */
  fixed: Effect[];
  /** q で大きさが変わる部分 */
  scale: OmamoriScale[];
  flag?: string;
  /** 'drop' = 遊んで手に入る（おまもりガチャ・ボス初回・星・図鑑・錬成）。'gaiden' = 外伝 */
  source: 'drop' | 'gaiden';
  /** 外伝品：同じ性能の元のお守り */
  sameAs?: string;
  /** 互換：旧段 stage（1..5）の効果。q = (stage-1)/4 と同じ */
  effects: (stage: number) => Effect[];
  /** 互換（旧段の最大） */
  maxStage: number;
  /** 説明（q=0.5 の値。一覧の見出し用） */
  desc: string;
}

const d = (id: string, name: string, rule: string, fixed: Effect[], scale: OmamoriScale[], flag?: string): OmamoriDef => {
  const def: OmamoriDef = { id, name, rule, fixed, scale, flag, source: 'drop', maxStage: 5, effects: () => [], desc: '' };
  def.effects = (stage) => mainEffectsOf(def, stageToQ(Math.max(1, Math.min(5, stage))));
  def.desc = mainText(def, 0.5);
  return def;
};

const BASE_OMAMORI: OmamoriDef[] = [
  d('sai2', '賽の目守', '移動の最低歩数+1', [{ key: 'diceMin', value: 1 }], [{ key: 'atk', lo: 0.01, hi: 0.05 }], 'omamori:sai2'),
  // 建築費-%（2〜10%）は外した（2026-10-22）：建物の陣材が小さい整数で、四捨五入で消えて効かなかった
  d('nidobushin', '二度建て守', '1手番に2回建てられる', [{ key: 'perTurn', value: 1 }], [], 'omamori:nidobushin'),
  d('hayagaeri', '早帰り守', '復活が1手番早い', [{ key: 'respawn', value: -1 }], [{ key: 'hp', lo: 0.02, hi: 0.1 }]),
  // ルール変化の無いお守り（数値だけ）は、枠が1つになったので主効果の幅を約1.6倍に（2026-10-21。ルール変化のお守りはそのまま）
  d('jintori', '陣取り守', '', [], [{ key: 'territory', lo: 0.08, hi: 0.14 }]),
  d('kusuribukuro', '薬袋', '回復薬+1', [{ key: 'potionCount', value: 1 }], [{ key: 'potionHeal', lo: 0.04, hi: 0.2 }]),
  d('shirozeme', '城攻め守', '', [], [{ key: 'core', lo: 0.08, hi: 0.21 }]),
  d('onitaiji', '鬼退治守', '', [], [{ key: 'boss', lo: 0.1, hi: 0.22 }]),
  d('dodai', '土台守', '', [], [{ key: 'buildHp', lo: 0.16, hi: 0.42 }]),
  d('takuwae', '蓄え守', '', [], [{ key: 'matStart', lo: 5, hi: 11, unit: 'int' }]),
  d('kikkou', '亀甲守', '', [], [{ key: 'taken', lo: -0.065, hi: -0.13 }]),
  d('yokebarai', '妖除け守', '', [], [{ key: 'neutral', lo: 0.1, hi: 0.22 }]),
  // 移動+1（毎手番）は多くの型で一番強かった（BALANCE.md 34章）ので、手番の初めに確率で+1に（確率は battle の flags.kazaguruma）
  d('kazaguruma', '風車守', '手番の初めに60%で移動+1', [], [{ key: 'agi', lo: 0.01, hi: 0.05 }], 'omamori:kazaguruma'),
  // 賽3つ（rules 2）：賽の合計・役で効く
  d('chouhan', '丁半守', '', [], [{ key: 'diceEven', lo: 0.08, hi: 0.2 }, { key: 'diceOdd', lo: 0.06, hi: 0.14 }]),
  d('yakumono', '役物守', '役が出ると陣材+2', [{ key: 'diceRoleMat', value: 2 }], [{ key: 'diceRole', lo: 0.3, hi: 0.6 }]),
  d('saijutsu', '賽術守', '', [], [{ key: 'diceSkill', lo: 0.4, hi: 1 }]),
];

/** 外伝の報酬（キャラごと）。見た目違いで、性能は元のお守りと同じ */
const GAIDEN: [string, string, string][] = [
  ['g_akari', '灯の守', 'jintori'],
  ['g_kazamaru', '羽の守', 'kazaguruma'],
  ['g_suzune', '金棒の守', 'shirozeme'],
  ['g_yuzuha', '狐の守', 'hayagaeri'],
];

export const OMAMORI: Record<string, OmamoriDef> = Object.fromEntries([
  ...BASE_OMAMORI.map((x) => [x.id, x]),
  ...GAIDEN.map(([id, name, same]) => {
    const base = BASE_OMAMORI.find((x) => x.id === same)!;
    return [id, { ...base, id, name, source: 'gaiden', sameAs: same } as OmamoriDef];
  }),
]);
/** 遊んで手に入る主効果（12種） */
export const DROP_OMAMORI_IDS = BASE_OMAMORI.map((x) => x.id);
/** 同じ効果の種類（外伝品は元のお守り） */
export const omamoriKind = (defId: string) => OMAMORI[defId]?.sameAs ?? defId;

export const RARITY_LABEL: Record<Rarity, string> = { n: '並', r: '良', sr: '優', ssr: '極' };

// ---------- 主効果 ----------


/** 旧お守りの段 stage（1..5）に当たる品質 q（q=1 が段 1+4×mainSpan） */
export const stageToQ = (stage: number) => clamp01((stage - 1) / (4 * CONFIG.omamori.mainSpan));

export function mainEffectsOf(def: OmamoriDef, q: number): Effect[] {
  const t = clamp01(q) * CONFIG.omamori.mainSpan;
  return [
    ...def.fixed.map((e) => ({ ...e })),
    ...def.scale.map((s) => {
      const v = (s.lo + (s.hi - s.lo) * t) * CONFIG.omamori.mainMult;
      return { key: s.key, value: s.unit === 'int' ? Math.round(v) : r3(v) };
    }),
  ];
}

/** 効果1つの表示（例「陣取り率+6.5%」「被ダメ-5%」「初期陣材+5」） */
export function effectText(e: Effect): string {
  const sign = e.value >= 0 ? '+' : '-';
  return `${MOD_LABEL[e.key]}${sign}${isPct(e.key) ? pct(Math.abs(e.value)) : Math.abs(e.value)}`;
}

/** 主効果の説明（ルール変化＋数値） */
export function mainText(def: OmamoriDef, q: number): string {
  const nums = mainEffectsOf(def, q).slice(def.fixed.length).map(effectText);
  return [def.rule, ...nums].filter(Boolean).join('・');
}

/** 主効果の幅の表示（例「陣地率+5〜9%」） */
export function mainRangeText(defId: string): string {
  const def = OMAMORI[defId];
  if (!def) return '';
  return def.scale
    .map((s) => {
      const hi = (s.lo + (s.hi - s.lo) * CONFIG.omamori.mainSpan) * CONFIG.omamori.mainMult;
      const a = s.unit === 'int' ? Math.round(Math.abs(s.lo * CONFIG.omamori.mainMult)) : Math.abs(r3(s.lo * CONFIG.omamori.mainMult)), b = s.unit === 'int' ? Math.round(Math.abs(hi)) : Math.abs(r3(hi));
      const sign = s.hi >= 0 ? '+' : '-';
      return `${MOD_LABEL[s.key]}${sign}${s.unit === 'int' ? `${a}〜${b}` : `${pct(a).replace('%', '')}〜${pct(b)}`}`;
    })
    .join('・');
}

// ---------- 副効果 ----------

export const OMAMORI_SUB_IDS: readonly OmamoriSubId[] = ['atk', 'hp', 'def', 'agi', 'skillPower', 'buildHp', 'buildPower', 'core', 'neutral', 'surge'];
export const SUB_LABEL: Record<OmamoriSubId, string> = {
  atk: '攻撃',
  hp: 'HP',
  def: '防御',
  agi: '速さ',
  skillPower: '術の威力',
  buildHp: '建物HP',
  buildPower: '建物の効果',
  core: '本陣への与ダメ',
  neutral: '中立への与ダメ',
  surge: '妖力の溜まりやすさ',
};
/** Lv の無い副効果（妖力の溜まりやすさ：最初の手番に妖力の撃破ポイント+1、装備全体で1つ分） */
export const isFixedSub = (id: OmamoriSubId) => id === 'surge';

/** 副効果の値（% は 0.05 = 5%。surge は点） */
export function subValue(s: OmamoriSub): number {
  const c = CONFIG.omamori.subs[s.id];
  if (!c) return 0;
  if (isFixedSub(s.id)) return c.per;
  return r3(c.per * Math.max(1, Math.min(5, s.lv)) * CONFIG.omamori.subMult);
}

/** 副効果の表示（例「攻撃+2.4%」「妖力の溜まりやすさ（最初の手番に+1点）」）。妖力には Lv を付けない */
export function subText(s: OmamoriSub): string {
  if (isFixedSub(s.id)) return `${SUB_LABEL[s.id]}（最初の手番に+${subValue(s)}点）`;
  return `${SUB_LABEL[s.id]}+${pct(subValue(s))}`;
}

function rollSubs(h: RngHolder, rarity: Rarity): OmamoriSub[] {
  const R = CONFIG.omamori.rarity[rarity];
  const [lo, hi] = R.subs;
  const n = lo + randInt(h, hi - lo + 1);
  const out: OmamoriSub[] = [];
  const lvW = CONFIG.omamori.lvWeights.slice(0, R.lvMax);
  for (let i = 0; i < n; i++) {
    const pool = OMAMORI_SUB_IDS.filter((id) => !out.some((x) => x.id === id) && (id !== 'surge' || rarity === 'sr' || rarity === 'ssr'));
    if (!pool.length) break;
    const id = weighted(h, pool.map((x) => ({ item: x, weight: CONFIG.omamori.subs[x]?.w ?? 1 })));
    const lv = isFixedSub(id) ? 1 : 1 + weighted(h, lvW.map((w, j) => ({ item: j, weight: w })));
    out.push({ id, lv });
  }
  return out;
}

// ---------- 生成 ----------

/** 生成したお守り（まだ所持品に入れていないもの）。報酬・ガチャの結果に入る */
export interface OmamoriRoll {
  defId: string;
  rarity: Rarity;
  q: number;
  subs: OmamoriSub[];
}

export interface RollOmamoriOpts {
  /** レア度を決める（無ければ rarityWeights から） */
  rarity?: Rarity;
  rarityWeights?: Partial<Record<Rarity, number>>;
  /** 主効果を決める */
  defId?: string;
  /** 主効果の候補（重み付き）。無ければ12種を等確率 */
  kinds?: { item: string; weight: number }[];
}

/** 既定のレア度の重み（ボス初回・星箱・図鑑など、ガチャ以外の入手先）：ガチャのランクB と同じ */
export const defaultRarityWeights = () => CONFIG.gacha.rarityByFortune.kichi as Record<Rarity, number>;

export function rollOmamori(h: RngHolder, o: RollOmamoriOpts = {}): OmamoriRoll {
  const w = o.rarityWeights ?? defaultRarityWeights();
  const rarity = o.rarity ?? weighted<Rarity>(h, RARITIES.map((x) => ({ item: x, weight: w[x] ?? 0 })));
  const defId = o.defId ?? (o.kinds?.length ? weighted(h, o.kinds) : DROP_OMAMORI_IDS[randInt(h, DROP_OMAMORI_IDS.length)]);
  const [qa, qb] = CONFIG.omamori.rarity[rarity].q;
  const q = Math.round((qa + (qb - qa) * random(h)) * 100) / 100;
  return { defId, rarity, q, subs: rollSubs(h, rarity) };
}

/** お守りの所持の上限 */
export const omamoriCap = () => CONFIG.omamori.inventory;
/**
 * 生成したお守りを所持品に入れる。所持の上限なら、いちばん弱いお守り（レア度の低い順・同じなら弱い順。ロック中・装備中・購入品は除く）を
 * 売値の霊玉に換えて入れる。新しいお守りの方が弱ければ、新しい方を霊玉に（item は null）。霊玉は呼び出し側が資源に足す
 */
export function addOmamori(p: Profile, roll: OmamoriRoll, extra: Partial<OmamoriItem> = {}): { item: OmamoriItem | null; goshuin: number; reidama: number; sold?: OmamoriItem } {
  if (!OMAMORI[roll.defId]) throw new Error(`unknown omamori ${roll.defId}`);
  let reidama = 0;
  let sold: OmamoriItem | undefined;
  if (p.omamori.length >= CONFIG.omamori.inventory && !extra.paid) {
    const worst = p.omamori
      .filter((o) => !o.locked && !o.paid && !omamoriOwner(p, o.id))
      .reduce<OmamoriItem | null>((w, o) => (!w || omamoriScore(o) < omamoriScore(w) ? o : w), null);
    const fresh = { id: -1, ...roll } as OmamoriItem;
    if (!worst || omamoriScore(fresh) <= omamoriScore(worst)) return { item: null, goshuin: 0, reidama: omamoriSellValue(roll) };
    p.omamori = p.omamori.filter((o) => o.id !== worst.id);
    reidama = omamoriSellValue(worst);
    sold = worst;
  }
  const item: OmamoriItem = { id: p.nextId++, defId: roll.defId, rarity: roll.rarity, q: roll.q, subs: roll.subs.map((s) => ({ ...s })), ...extra };
  p.omamori.push(item);
  return { item, goshuin: 0, reidama, ...(sold ? { sold } : {}) };
}

/**
 * 互換の入口（受け取り箱の omamori:<id>・外伝の報酬・旧課金品）：主効果 defId のお守りを1つ作る。
 * paid は旧課金品（完成品として q を高めに、売却・錬成できない）。同じ課金品は1つだけ
 */
export function grantOmamori(p: Profile, defId: string, paid = false, h?: RngHolder): { item: OmamoriItem; upgraded: boolean; wasted: boolean } {
  if (!OMAMORI[defId]) throw new Error(`unknown omamori ${defId}`);
  if (paid) {
    const owned = p.omamori.find((x) => x.defId === defId && x.paid);
    if (owned) return { item: owned, upgraded: false, wasted: true };
    const item = addOmamori(p, { defId, rarity: 'sr', q: CONFIG.omamori.legacyPaidQ, subs: [] }, { paid: true }).item!;
    return { item, upgraded: false, wasted: false };
  }
  const roll = h ? rollOmamori(h, { defId, rarity: 'r' }) : { defId, rarity: 'r' as Rarity, q: 0.5, subs: [] };
  const r = addOmamori(p, roll);
  return { item: r.item ?? { id: 0, ...roll }, upgraded: false, wasted: !r.item };
}

// ---------- 装備・効果 ----------

/**
 * キャラ charId の枠 slot（0..OMAMORI_SLOTS-1。今は 0 だけ）に装備。同じキャラに同じ主効果（sameAs を含む）は2つ付けられない。null で外す。
 * 他のキャラに付いている個体なら、そのキャラから外して付け替える（装備と同じ）
 */
export function equipOmamori(p: Profile, charId: CharId, slot: number, omamoriId: number | null): OpResult {
  const set = p.omamoriSets[charId];
  if (!set) return { ok: false, error: 'char' };
  if (!Number.isInteger(slot) || slot < 0 || slot >= OMAMORI_SLOTS || slot >= set.length) return { ok: false, error: 'slot' };
  if (omamoriId === null) {
    set[slot] = null;
    return { ok: true };
  }
  const item = p.omamori.find((x) => x.id === omamoriId);
  if (!item) return { ok: false, error: 'no_item' };
  for (let i = 0; i < set.length; i++) {
    if (i === slot || set[i] === null || set[i] === item.id) continue;
    const other = p.omamori.find((x) => x.id === set[i]);
    if (other && omamoriKind(other.defId) === omamoriKind(item.defId)) return { ok: false, error: 'same_kind' };
  }
  for (const c of CHAR_IDS) {
    const s = p.omamoriSets[c];
    for (let i = 0; i < s.length; i++) if (s[i] === item.id) s[i] = null;
  }
  set[slot] = omamoriId;
  return { ok: true };
}

export function equippedOmamori(p: Profile, charId: CharId): OmamoriItem[] {
  // 旧データの移行中（normalizeProfile が normalizeOmamori より前に戦力を測る）は omamoriSets がまだ無い
  return (p.omamoriSets?.[charId] ?? []).map((id) => p.omamori.find((x) => x.id === id)).filter((x): x is OmamoriItem => !!x);
}

/** お守り id を付けているキャラ（無ければ null） */
export function omamoriOwner(p: Profile, id: number): CharId | null {
  return CHAR_IDS.find((c) => p.omamoriSets[c]?.includes(id)) ?? null;
}

/** どれかのキャラに付いていれば true */
export const isOmamoriEquipped = (p: Profile, id: number) => omamoriOwner(p, id) !== null;

/**
 * キャラ charId が装備中のお守りの効果の合計。副効果は効果ごとに cap（×subMult。Lv5 より上なので1つでは当たらない）まで。
 * surge（妖力の溜まりやすさ）は flags の comp:surge に足す点数（そのキャラの装備全体で cap まで）
 */
export function omamoriMods(p: Profile, charId: CharId): { mods: Mods; flags: string[]; surge: number } {
  const mods: Mods = {};
  const flags: string[] = [];
  const subSum: Partial<Record<OmamoriSubId, number>> = {};
  for (const o of equippedOmamori(p, charId)) {
    const def = OMAMORI[o.defId];
    if (!def) continue;
    for (const e of mainEffectsOf(def, o.q)) addMod(mods, e.key, e.value);
    if (def.flag) flags.push(def.flag);
    for (const s of o.subs ?? []) subSum[s.id] = (subSum[s.id] ?? 0) + subValue(s);
  }
  let surge = 0;
  for (const [id, v] of Object.entries(subSum) as [OmamoriSubId, number][]) {
    const c = CONFIG.omamori.subs[id];
    if (!c) continue;
    if (isFixedSub(id)) surge = Math.min(c.cap, v);
    else addMod(mods, id as ModKey, r3(Math.min(c.cap * CONFIG.omamori.subMult, v)));
  }
  return { mods, flags, surge };
}

/** 一覧・詳細の説明（主効果・副効果） */
export function omamoriView(item: OmamoriItem) {
  const def = OMAMORI[item.defId];
  return {
    name: def?.name ?? item.defId,
    rarity: item.rarity,
    rarityLabel: RARITY_LABEL[item.rarity] ?? '並',
    main: def ? mainText(def, item.q) : '',
    range: mainRangeText(item.defId),
    q: item.q,
    subs: (item.subs ?? []).map((s) => ({ ...s, text: subText(s), fixed: isFixedSub(s.id) })),
  };
}

/** 強さの目安（並べ替え用。主効果の q とレア度・副効果の Lv の合計） */
export const omamoriScore = (o: OmamoriItem) => RARITIES.indexOf(o.rarity) * 10 + o.q * 5 + (o.subs ?? []).reduce((a, s) => a + (isFixedSub(s.id) ? 3 : s.lv), 0);

// ---------- ロック・売却・錬成 ----------

/** 素材にできない理由（null ならできる）。ロック中・装備中・旧課金品 */
export function omamoriBlock(p: Profile, o: OmamoriItem): 'locked' | 'equipped' | 'paid' | null {
  if (o.locked) return 'locked';
  if (isOmamoriEquipped(p, o.id)) return 'equipped';
  if (o.paid) return 'paid';
  return null;
}

export function setOmamoriLock(p: Profile, id: number, locked: boolean): OpResult<{ locked: boolean }> {
  const o = p.omamori.find((x) => x.id === id);
  if (!o) return { ok: false, error: 'no_item' };
  if (locked) o.locked = true;
  else delete o.locked;
  return { ok: true, locked: !!o.locked };
}

/** 売値（霊玉） */
export const omamoriSellValue = (o: Pick<OmamoriItem, 'rarity'>) => CONFIG.omamori.sellReidama[o.rarity] ?? 0;

/**
 * 指定したお守りを霊玉で売る。1つでもロック中・装備中・旧課金品・無いものが含まれていれば何もせずに断る（画面の選択ずれで大事なものを売らない）
 */
export function sellOmamori(p: Profile, res: Resources, ids: number[]): OpResult<{ count: number; reidama: number; ids: number[] }> {
  const want = [...new Set(ids)];
  if (!want.length) return { ok: false, error: 'empty' };
  const items: OmamoriItem[] = [];
  for (const id of want) {
    const o = p.omamori.find((x) => x.id === id);
    if (!o) return { ok: false, error: 'no_item' };
    const b = omamoriBlock(p, o);
    if (b) return { ok: false, error: b };
    items.push(o);
  }
  let reidama = 0;
  for (const o of items) reidama += omamoriSellValue(o);
  const gone = new Set(want);
  p.omamori = p.omamori.filter((x) => !gone.has(x.id));
  res.reidama += reidama;
  return { ok: true, count: items.length, reidama, ids: want };
}

/** 一括売却の対象：このレア度以下で、ロック中・装備中・旧課金品でないもの */
export function omamoriSellTargets(p: Profile, maxRarity: Rarity): OmamoriItem[] {
  const max = RARITIES.indexOf(maxRarity);
  if (max < 0) return [];
  return p.omamori.filter((o) => RARITIES.indexOf(o.rarity) <= max && !omamoriBlock(p, o));
}

export function sellOmamoriByRarity(p: Profile, res: Resources, maxRarity: Rarity) {
  const ids = omamoriSellTargets(p, maxRarity).map((o) => o.id);
  if (!ids.length) return { ok: true as const, count: 0, reidama: 0, ids: [] as number[] };
  return sellOmamori(p, res, ids);
}

/** 錬成の結果のレア度の重み（素材のレア度の平均の切り捨てから そのまま／+1／+2） */
export function fuseRarityWeights(rarities: Rarity[]): Record<Rarity, number> {
  const avg = Math.floor(rarities.reduce((a, r) => a + RARITIES.indexOf(r), 0) / Math.max(1, rarities.length));
  const out = { n: 0, r: 0, sr: 0, ssr: 0 } as Record<Rarity, number>;
  CONFIG.omamori.fuseShift.forEach((w, i) => {
    const r = RARITIES[Math.min(RARITIES.length - 1, avg + i)];
    out[r] += w;
  });
  // 極は出にくく：減らした分は素材と同じレア度（そのまま）へ
  const cut = out.ssr * (1 - CONFIG.omamori.fuseSsrMult);
  out.ssr -= cut;
  out[RARITIES[Math.min(RARITIES.length - 1, avg)]] += cut;
  return out;
}

export const fuseCost = (p: Profile) => ((p.tickets?.fuse ?? 0) > 0 ? { zeni: 0, ticket: true } : { zeni: CONFIG.omamori.fuseZeni, ticket: false });

/**
 * 錬成：要らないお守り3つ＋銭（錬成札があれば札1枚）で新しいお守りを1つ作る。
 * 主効果は素材3つのどれか（等確率）、レア度は fuseRarityWeights。素材はロック中・装備中・旧課金品・重複を必ず弾く
 */
export function fuseOmamori(p: Profile, res: Resources, ids: number[], h: RngHolder): OpResult<{ item: OmamoriItem; roll: OmamoriRoll; consumed: number[]; cost: { zeni: number; ticket: boolean } }> {
  if (!Array.isArray(ids) || ids.length !== 3 || new Set(ids).size !== 3) return { ok: false, error: 'need3' };
  const mats: OmamoriItem[] = [];
  for (const id of ids) {
    const o = p.omamori.find((x) => x.id === id);
    if (!o) return { ok: false, error: 'no_item' };
    const b = omamoriBlock(p, o);
    if (b) return { ok: false, error: b };
    mats.push(o);
  }
  const cost = fuseCost(p);
  if (res.zeni < cost.zeni) return { ok: false, error: 'zeni' };
  const kind = omamoriKind(mats[randInt(h, mats.length)].defId);
  const roll = rollOmamori(h, { defId: kind, rarityWeights: fuseRarityWeights(mats.map((m) => m.rarity)) });
  res.zeni -= cost.zeni;
  if (cost.ticket) p.tickets.fuse = Math.max(0, (p.tickets.fuse ?? 0) - 1);
  const gone = new Set(ids);
  p.omamori = p.omamori.filter((x) => !gone.has(x.id));
  const item = addOmamori(p, roll).item!; // 3つ消して1つ足すので所持の上限は超えない
  return { ok: true, item, roll, consumed: ids.slice(), cost };
}

// ---------- 旧データの移行 ----------

/**
 * 固定12種のお守り（段 stage・課金品 paid）を「主効果だけ・効果の大きさ中央」の個体に変換する（normalizeProfile が呼ぶ）。
 * 課金で買った完成品は q を高め（legacyPaidQ）・優として扱い、売却・錬成はできない。ロックはそのまま。
 * 装備枠はキャラごと（omamoriSets）に OMAMORI_SLOTS（1つ）。旧の共通の枠（omamoriSlots）は出撃キャラ oshi の枠へ移し、他のキャラは空。
 * 無い id・同じキャラに同じ id・複数のキャラに付いた id は外す（先に出てきたキャラだけ残す）。
 * 旧の3枠に2つ以上付いていたキャラは、score（そのキャラの戦力。normalizeProfile が powerOf を渡す）が最も高くなる1つだけ残す。
 * score が無ければ先頭の1つ
 */
export function normalizeOmamori(p: Profile, score?: (p: Profile, charId: CharId) => number) {
  if (!Array.isArray(p.omamori)) p.omamori = [];
  const ok: OmamoriItem[] = [];
  for (const raw of p.omamori as (OmamoriItem & { stage?: number })[]) {
    if (!raw || typeof raw !== 'object' || !OMAMORI[raw.defId]) continue;
    if (!RARITIES.includes(raw.rarity) || typeof raw.q !== 'number') {
      const paid = !!raw.paid;
      raw.rarity = paid ? 'sr' : 'n';
      raw.q = paid ? CONFIG.omamori.legacyPaidQ : CONFIG.omamori.legacyQ;
      raw.subs = [];
      raw.legacy = true;
    }
    if ('stage' in raw) delete raw.stage;
    if (!raw.paid) delete raw.paid;
    if (!Array.isArray(raw.subs)) raw.subs = [];
    raw.subs = raw.subs.filter((s) => s && OMAMORI_SUB_IDS.includes(s.id)).map((s) => ({ id: s.id, lv: Math.max(1, Math.min(5, Math.round(Number(s.lv) || 1))) }));
    raw.q = clamp01(raw.q);
    ok.push(raw);
  }
  p.omamori = ok;
  const legacy = p as Profile & { omamoriSlots?: unknown };
  if (!p.omamoriSets || typeof p.omamoriSets !== 'object') {
    const old = Array.isArray(legacy.omamoriSlots) ? legacy.omamoriSlots : [];
    p.omamoriSets = Object.fromEntries(CHAR_IDS.map((c) => [c, c === p.oshi ? old.slice(0, 3) : []])) as Profile['omamoriSets'];
  }
  if ('omamoriSlots' in legacy) delete legacy.omamoriSlots;
  // 旧の枠の数は3（3 → 1 は 2026-10-21）。まず各キャラの候補（重複・無い id を除く）をそろえ、全員の枠を空にしてから1つずつ選ぶ
  const used = new Set<number>();
  const cands = {} as Record<CharId, number[]>;
  for (const c of CHAR_IDS) {
    const raw = Array.isArray(p.omamoriSets[c]) ? p.omamoriSets[c] : [];
    cands[c] = [];
    for (const id of raw.slice(0, 3)) {
      if (typeof id !== 'number' || used.has(id) || !ok.some((x) => x.id === id)) continue;
      used.add(id);
      cands[c].push(id);
    }
  }
  for (const c of CHAR_IDS) p.omamoriSets[c] = Array<number | null>(OMAMORI_SLOTS).fill(null);
  for (const c of CHAR_IDS) {
    const list = cands[c];
    if (!list.length) continue;
    let best = list[0];
    if (score && list.length > 1) {
      let bestPw = -Infinity;
      for (const id of list) {
        p.omamoriSets[c][0] = id;
        const pw = score(p, c);
        if (pw > bestPw) {
          bestPw = pw;
          best = id;
        }
      }
    }
    p.omamoriSets[c][0] = best;
  }
  if (!p.omikuji || typeof p.omikuji !== 'object') p.omikuji = { freeDay: '', perk: null, luckDay: '', free: 0 };
  // 御朱印・交換所は廃止（2026-10-31）。保存データの互換のため項目だけ残す（増える経路も使う経路も無い）
  if (typeof p.goshuin !== 'number' || !Number.isFinite(p.goshuin)) p.goshuin = 0;
  if (!p.exchange || typeof p.exchange !== 'object') p.exchange = { week: '', bought: {} };
  if (!Array.isArray(p.fuseLog)) p.fuseLog = [];
}
